import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Play, Pause, SkipBack, SkipForward, Maximize, Minimize, BookmarkPlus, Pencil } from 'lucide-react';
import './SynchronizedPosePlayer.css';

const SynchronizedPosePlayer = forwardRef(({
  videoId,
  analysisData,
  onFrameChange,
  onBookmark,
  hideControls = false,
  drawMode = false,
  onDrawToggle,
}, ref) => {
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [videoMetadata, setVideoMetadata] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false); // NEW: Prevent timeUpdate interference during seeks

  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Overlay controls auto-hide state (landscape fullscreen)
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimerRef = useRef(null);

  // Mobile swipe gesture state
  const [swipeStart, setSwipeStart] = useState(null);
  const [swipeEnd, setSwipeEnd] = useState(null);
  const minSwipeDistance = 50;

  // Refs
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const seekFnRef = useRef(null);
  const seekAsyncFnRef = useRef(null);
  const pendingPlayPromiseRef = useRef(null);
  const pendingPlayRequestedRef = useRef(false);
  const playRequestTokenRef = useRef(0);

  const pauseVideoSafely = useCallback(() => {
    const video = videoRef.current;
    const shouldInterruptPendingPlay =
      pendingPlayRequestedRef.current || pendingPlayPromiseRef.current !== null;

    playRequestTokenRef.current += 1;
    pendingPlayRequestedRef.current = false;
    pendingPlayPromiseRef.current = null;

    if (video && (!video.paused || shouldInterruptPendingPlay)) {
      video.pause();
    }

    setIsPlaying(false);
  }, []);

  const playVideoSafely = useCallback(async (reason = 'playback') => {
    const video = videoRef.current;
    if (!video) return false;

    if (pendingPlayRequestedRef.current) {
      if (pendingPlayPromiseRef.current) {
        try {
          await pendingPlayPromiseRef.current;
        } catch {
          return false;
        }
      }
      return true;
    }

    if (!video.paused && !video.ended) {
      return true;
    }

    const requestToken = playRequestTokenRef.current + 1;
    playRequestTokenRef.current = requestToken;
    pendingPlayRequestedRef.current = true;

    let playPromise;

    try {
      playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        pendingPlayPromiseRef.current = playPromise;
        await playPromise;
        if (playRequestTokenRef.current === requestToken && pendingPlayPromiseRef.current === playPromise) {
          pendingPlayPromiseRef.current = null;
        }
      }
      return true;
    } catch (error) {
      if (playRequestTokenRef.current === requestToken) {
        pendingPlayPromiseRef.current = null;
        pendingPlayRequestedRef.current = false;
      }

      const isAbortError =
        error?.name === 'AbortError' ||
        /aborted/i.test(String(error?.message || ''));

      if (isAbortError) {
        console.warn(`Playback request was interrupted before start (${reason}).`, error);
        return false;
      }

      console.error(`Playback request failed (${reason}):`, error);
      return false;
    }
  }, []);

  // Expose imperative API for comparison viewer
  useImperativeHandle(ref, () => ({
    seekToFrame: (frame) => seekFnRef.current?.(frame),
    seekToFrameAsync: (frame) => seekAsyncFnRef.current?.(frame),
    seekByFrames: (offset) => {
      const video = videoRef.current;
      if (!video) return;
      seekFnRef.current?.(Math.max(0, (videoRef.current._currentFrame || 0) + offset));
    },
    play: () => {
      void playVideoSafely('imperative play');
    },
    pause: () => {
      pauseVideoSafely();
    },
    getCurrentFrame: () => currentFrame,
    getVideoMetadata: () => videoMetadata,
    isVideoPlaying: () => isPlaying,
    getContainerElement: () => containerRef.current?.querySelector('.video-container'),
  }), [currentFrame, videoMetadata, isPlaying, pauseVideoSafely, playVideoSafely]);

  // Handle video errors
  const handleVideoError = (event) => {
    const video = videoRef.current;
    if (video && video.error) {
      console.error('❌ Video error:', {
        code: video.error.code,
        message: video.error.message,
        src: video.src,
        networkState: video.networkState,
        readyState: video.readyState
      });

      // Error codes:
      // 1 = MEDIA_ERR_ABORTED
      // 2 = MEDIA_ERR_NETWORK
      // 3 = MEDIA_ERR_DECODE
      // 4 = MEDIA_ERR_SRC_NOT_SUPPORTED

      if (video.error.code === 4) {
        console.error('💥 Video source not supported. Check:');
        console.error('  - Video URL:', analysisData?.videoUrl);
        console.error('  - Video ID:', videoId);
        console.error('  - File exists on backend?');
        console.error('  - Is generation complete?');
      } else if (video.error.code === 2) {
        console.error('🌐 Network error loading video');
        console.error('  - Check if backend is running');
        console.error('  - Check if file exists');
      }
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener('error', handleVideoError);
      return () => video.removeEventListener('error', handleVideoError);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Video event handlers
  const handleVideoLoad = () => {
    const video = videoRef.current;

    if (video) {
      console.log('📹 Video loaded:', {
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        src: video.currentSrc
      });

      setDuration(video.duration);

      // Set video metadata for accurate frame calculations
      // Use actual frame count from analysis data if available, otherwise estimate from video duration
      const actualFrameCount = analysisData?.frame_count;
      let fps, totalFrames;

      if (actualFrameCount && actualFrameCount > 0 && video.duration > 0) {
        // Calculate actual FPS from frame count and duration
        totalFrames = actualFrameCount;
        fps = actualFrameCount / video.duration;
        console.log(`📊 Using actual frame count: ${totalFrames} frames, calculated FPS: ${fps.toFixed(2)}`);
      } else {
        // Fallback to 30 FPS estimate
        fps = 30;
        totalFrames = Math.floor(video.duration * fps);
        console.log(`⚠️ Using estimated 30 FPS: ${totalFrames} frames`);
      }

      // Extract frame offset from pose_data for absolute frame number display
      // The burn-in video shows absolute frame numbers (e.g., 40/96)
      // but video playback starts at relative frame 0
      // We need the offset to convert relative -> absolute frame numbers
      const poseData = analysisData?.pose_data || [];
      const startFrame = poseData.length > 0 && poseData[0].frame_index != null
        ? poseData[0].frame_index
        : 0;

      setVideoMetadata({
        fps: fps,
        total_frames: totalFrames,
        duration: video.duration,
        startFrame: startFrame  // Offset for absolute frame number calculation
      });

      console.log('✅ Video metadata set:', {
        fps: fps.toFixed(2),
        totalFrames,
        duration: video.duration,
        startFrame: startFrame
      });
    }
  };

  // Precise frame calculation using actual video metadata
  // Returns frame number matching burn-in overlay display (starts at 0)
  // Note: Burn-in video always starts at frame 0, no offset needed
  const calculateFrameNumber = (currentTime) => {
    if (!videoMetadata) return Math.floor(currentTime * 30); // Fallback to 30 FPS

    const fps = videoMetadata.fps || 30;
    const totalFrames = videoMetadata.total_frames || Math.floor(duration * fps);

    // Frame number = floor(time * fps), matching burn-in overlay which starts at 0
    const frameNumber = Math.floor(currentTime * fps);

    // Clamp to valid range (0 to totalFrames - 1)
    return Math.max(0, Math.min(frameNumber, totalFrames - 1));
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    // Don't update during manual seeks or dragging
    if (video && !isDragging && !isSeeking) {
      const newTime = video.currentTime;
      const newFrame = calculateFrameNumber(newTime);

      setCurrentTime(newTime);

      // Only update if frame changed
      if (newFrame !== currentFrame) {
        setCurrentFrame(newFrame);

        // Notify parent component of frame change
        if (onFrameChange) {
          onFrameChange(newFrame, null);
        }
      }
    }
  };
  
  // Playback controls
  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying || pendingPlayRequestedRef.current) {
        pauseVideoSafely();
      } else {
        void playVideoSafely('play toggle');
      }
    }
  }, [isPlaying, pauseVideoSafely, playVideoSafely]);
  
  const seekToFrame = (frameNumber) => {
    const video = videoRef.current;
    if (!video) {
      console.warn('Cannot seek to frame: video not ready');
      return;
    }

    const fps = videoMetadata?.fps || 30;
    // Add small epsilon to account for floating-point precision
    // This ensures video.currentTime is slightly past the frame boundary
    // so Math.floor() in calculateFrameNumber rounds to the correct frame
    const time = (frameNumber / fps) + 0.001;

    // Ensure time is within video bounds
    if (time >= 0 && time <= video.duration) {
      // Prevent timeUpdate from interfering
      setIsSeeking(true);

      video.currentTime = time;
      setCurrentFrame(frameNumber);
      setCurrentTime(time);

      // Notify parent component of frame change (even if no pose data at this frame)
      if (onFrameChange) {
        onFrameChange(frameNumber, null);
      }

      // Use ONLY timeout-based approach with longer duration
      // Remove 'seeked' event listener to prevent race condition
      setTimeout(() => {
        setIsSeeking(false);
      }, 400); // Increased from 200ms to 400ms
    } else {
      console.warn(`Cannot seek to frame ${frameNumber}: time ${time} out of bounds (0-${video.duration})`);
    }
  };
  // Keep ref current so useImperativeHandle always delegates to latest closure
  seekFnRef.current = seekToFrame;

  // Async version that resolves when the browser finishes seeking
  const seekToFrameAsync = (frameNumber) => {
    const video = videoRef.current;
    if (!video) return Promise.resolve();

    const fps = videoMetadata?.fps || 30;
    const time = (frameNumber / fps) + 0.001;

    if (time < 0 || time > video.duration) return Promise.resolve();

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        video.removeEventListener('seeked', onSeeked);
        setIsSeeking(false);
        resolve();
      }, 500);

      const onSeeked = () => {
        clearTimeout(timeout);
        video.removeEventListener('seeked', onSeeked);
        setIsSeeking(false);
        resolve();
      };
      video.addEventListener('seeked', onSeeked);

      setIsSeeking(true);
      video.currentTime = time;
      setCurrentFrame(frameNumber);
      setCurrentTime(time);

      if (onFrameChange) {
        onFrameChange(frameNumber, null);
      }
    });
  };
  seekAsyncFnRef.current = seekToFrameAsync;

  const seekByFrames = (frameOffset) => {
    const video = videoRef.current;
    if (!video || !videoMetadata) {
      console.warn('Cannot seek by frames: video or metadata not ready');
      return;
    }

    const fps = videoMetadata.fps || 30;
    const totalFrames = videoMetadata.total_frames || Math.floor(duration * fps);

    // Simple increment/decrement - allow navigation to ANY frame
    const newFrame = currentFrame + frameOffset;

    // Only enforce bounds (0 to totalFrames-1)
    const boundedFrame = Math.max(0, Math.min(newFrame, totalFrames - 1));

    console.log(`Seeking from frame ${currentFrame} to ${boundedFrame} (offset: ${frameOffset})`);

    seekToFrame(boundedFrame);
  };
  
  // Timeline scrubbing
  const handleTimelineClick = (event) => {
    const timeline = timelineRef.current;
    if (timeline) {
      const rect = timeline.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      const newFrame = calculateFrameNumber(newTime);

      seekToFrame(newFrame);
    }
  };
  
  const handleTimelineDrag = (event) => {
    if (!isDragging) return;

    const timeline = timelineRef.current;
    if (timeline) {
      const rect = timeline.getBoundingClientRect();
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      const newFrame = calculateFrameNumber(newTime);

      setCurrentTime(newTime);
      setCurrentFrame(newFrame);

      // Notify parent component
      if (onFrameChange) {
        onFrameChange(newFrame, null);
      }
    }
  };

  // Touch event handlers for timeline scrubbing (mobile support)
  const handleTimelineTouch = (touch) => {
    const timeline = timelineRef.current;
    if (timeline) {
      const rect = timeline.getBoundingClientRect();
      const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      const newFrame = calculateFrameNumber(newTime);

      setCurrentTime(newTime);
      setCurrentFrame(newFrame);

      // Notify parent component
      if (onFrameChange) {
        onFrameChange(newFrame, null);
      }
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent scroll while scrubbing
    setIsDragging(true);
    if (e.touches && e.touches[0]) {
      handleTimelineTouch(e.touches[0]);
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    if (e.touches && e.touches[0]) {
      handleTimelineTouch(e.touches[0]);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    // Sync video to final position
    if (videoRef.current && currentTime !== undefined) {
      videoRef.current.currentTime = currentTime;
    }
  };

  // Swipe gestures for video container (frame navigation)
  const onVideoTouchStart = (e) => {
    setSwipeEnd(null);
    if (e.targetTouches && e.targetTouches[0]) {
      setSwipeStart(e.targetTouches[0].clientX);
    }
  };

  const onVideoTouchMove = (e) => {
    if (e.targetTouches && e.targetTouches[0]) {
      setSwipeEnd(e.targetTouches[0].clientX);
    }
  };

  const onVideoTouchEnd = () => {
    if (drawMode) { setSwipeStart(null); setSwipeEnd(null); return; }
    if (!swipeStart || !swipeEnd) {
      // No movement - this is a tap. Toggle controls in any mode.
      if (controlsVisible) {
        setControlsVisible(false);
        if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      } else {
        showControlsTemporarily();
      }
      setSwipeStart(null);
      setSwipeEnd(null);
      return;
    }

    const distance = swipeStart - swipeEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      seekByFrames(1);
    } else if (isRightSwipe) {
      seekByFrames(-1);
    } else {
      // Small movement, treat as tap
      if (controlsVisible) {
        setControlsVisible(false);
        if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      } else {
        showControlsTemporarily();
      }
    }

    setSwipeStart(null);
    setSwipeEnd(null);
  };

  // Auto-hide controls overlay
  const showControlsTemporarily = useCallback(() => {
    setControlsVisible(prev => {
      if (!prev) return true;
      return prev;
    });
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      if (isPlaying) setControlsVisible(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      showControlsTemporarily();
    } else {
      setControlsVisible(true);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    }
    return () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, [isPlaying, showControlsTemporarily]);

  // Force controls visible during draw mode
  useEffect(() => {
    if (drawMode) {
      setControlsVisible(true);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    }
  }, [drawMode]);

  // Click-to-toggle controls (desktop mouse)
  const handleVideoContainerClick = useCallback(() => {
    if (drawMode) return;
    if (controlsVisible) {
      setControlsVisible(false);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    } else {
      showControlsTemporarily();
    }
  }, [drawMode, controlsVisible, showControlsTemporarily]);

  const handleContainerMouseEnter = useCallback(() => {
    showControlsTemporarily();
  }, [showControlsTemporarily]);

  const handleContainerMouseMove = useCallback(() => {
    if (isPlaying) showControlsTemporarily();
  }, [isPlaying, showControlsTemporarily]);

  const handleContainerMouseLeave = useCallback(() => {
    if (isPlaying) setControlsVisible(false);
  }, [isPlaying]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (event) => {
      switch (event.key) {
        case ' ':
          event.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          seekByFrames(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          seekByFrames(1);
          break;
        case 'j':
          seekByFrames(-10);
          break;
        case 'k':
          togglePlayPause();
          break;
        case 'l':
          seekByFrames(10);
          break;
        default:
          // Other keys are ignored
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentFrame]);

  // Fullscreen handling - platform-aware fullscreen strategies
  // Detect if device is mobile/touch
  const isMobileDevice = () => {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(max-width: 768px)').matches
    );
  };

  // Detect iOS Safari
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
    return isIOS || (isIOS && isSafari);
  };

  // Detect Android Chrome
  const isAndroidChrome = () => {
    const ua = navigator.userAgent;
    return /Android/.test(ua) && /Chrome/.test(ua);
  };

  // Check if native video fullscreen is available (iOS)
  const supportsNativeVideoFullscreen = () => {
    const video = videoRef.current;
    return video && (video.webkitSupportsFullscreen || video.webkitEnterFullscreen);
  };

  // Enter native video fullscreen (iOS Safari only option for true fullscreen)
  const enterNativeVideoFullscreen = () => {
    const video = videoRef.current;
    if (video) {
      // iOS requires video to be playing first
      const wasPlaying = !video.paused;
      if (!wasPlaying) {
        video.play().then(() => {
          if (video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
          } else if (video.webkitEnterFullScreen) {
            video.webkitEnterFullScreen(); // Note capital S
          }
        }).catch(err => {
          console.warn('Could not enter native fullscreen:', err);
          // Fallback to CSS fullscreen
          setIsFullscreen(true);
        });
      } else {
        if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
        } else if (video.webkitEnterFullScreen) {
          video.webkitEnterFullScreen();
        }
      }
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      // Exit fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (document.webkitFullscreenElement) {
        document.webkitExitFullscreen();
      }
      setControlsVisible(true);
      setIsFullscreen(false);
      return;
    }

    // Enter fullscreen - platform-specific strategies
    if (isAndroidChrome()) {
      // Android Chrome: Use Fullscreen API with navigationUI: "hide"
      // This hides the browser address bar and status bar
      const elem = containerRef.current;
      if (elem && elem.requestFullscreen) {
        elem.requestFullscreen({ navigationUI: "hide" })
          .then(() => setIsFullscreen(true))
          .catch(err => {
            console.warn('Fullscreen with navigationUI failed, trying standard:', err);
            // Fallback to standard fullscreen
            elem.requestFullscreen()
              .then(() => setIsFullscreen(true))
              .catch(() => setIsFullscreen(true)); // CSS fallback
          });
        return;
      }
    }

    if (isIOSSafari()) {
      // iOS Safari: Native video fullscreen is the only true fullscreen option
      // But it loses our custom controls. Offer both options.
      // For now, use CSS fullscreen (preserves controls but shows browser UI)
      // User can long-press for native fullscreen if needed
      setIsFullscreen(true);
      return;
    }

    if (!isMobileDevice()) {
      // Desktop: use native browser fullscreen API
      const elem = containerRef.current;
      if (elem) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen({ navigationUI: "hide" });
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen();
        }
      }
    } else {
      // Other mobile: CSS-based fullscreen
      setIsFullscreen(true);
    }
  };

  // Long-press handler for native fullscreen on iOS (optional escape hatch)
  const [longPressTimer, setLongPressTimer] = useState(null);

  const handleLongPressStart = () => {
    if (isIOSSafari() && supportsNativeVideoFullscreen()) {
      const timer = setTimeout(() => {
        enterNativeVideoFullscreen();
      }, 800); // 800ms long press
      setLongPressTimer(timer);
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Handle escape key and fullscreenchange events
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    // Sync state when fullscreen changes (e.g., user presses Esc on desktop)
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      if (!isNowFullscreen && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when in fullscreen
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  
  // Log video URL once when component mounts or URL changes
  useEffect(() => {
    if (analysisData?.videoUrl) {
      console.log('🎬 Video URL set:', analysisData.videoUrl);
    }
  }, [analysisData?.videoUrl]);

  return (
    <div
      className={`synchronized-pose-player ${isFullscreen ? 'inline-fullscreen' : ''}`}
      ref={containerRef}
      onMouseEnter={handleContainerMouseEnter}
      onMouseMove={handleContainerMouseMove}
      onMouseLeave={handleContainerMouseLeave}
    >
      {/* Video Container with swipe gesture support */}
      <div
        className="video-container"
        onClick={handleVideoContainerClick}
        onTouchStart={onVideoTouchStart}
        onTouchMove={onVideoTouchMove}
        onTouchEnd={onVideoTouchEnd}
      >
        <video
          ref={videoRef}
          className="main-video"
          onLoadedMetadata={handleVideoLoad}
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => {
            pendingPlayRequestedRef.current = false;
            pendingPlayPromiseRef.current = null;
            setIsPlaying(true);
          }}
          onPause={() => {
            pendingPlayRequestedRef.current = false;
            pendingPlayPromiseRef.current = null;
            setIsPlaying(false);
          }}
          onEnded={() => {
            pendingPlayRequestedRef.current = false;
            pendingPlayPromiseRef.current = null;
            setIsPlaying(false);
          }}
          onError={handleVideoError}
          src={analysisData?.videoUrl}
          playsInline
          webkitPlaysInline="true"
          controls={false}
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
        />
        {!analysisData?.videoUrl && (
          <div style={{ padding: '20px', color: 'red' }}>
            ⚠️ No video URL provided
          </div>
        )}
      </div>

      {/* Controls overlay - auto-hides when playing, hover/tap to show */}
      {!hideControls && (
      <div
        className={`controls-overlay overlay-mode ${controlsVisible ? 'visible' : 'hidden'}`}
        onTouchStart={(e) => { e.stopPropagation(); showControlsTemporarily(); }}
      >
        {/* Timeline with touch support */}
        <div className="timeline-container">
          <div
            className="timeline"
            ref={timelineRef}
            onClick={handleTimelineClick}
            onMouseDown={() => setIsDragging(true)}
            onMouseMove={handleTimelineDrag}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            <div className="timeline-track">
              <div
                className="timeline-progress"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />

              <div
                className="timeline-thumb"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Control buttons */}
        <div className="player-controls">
          <div className="main-controls">
            {/* Back 1 frame - always shown, use arrow symbol */}
            <button onClick={() => seekByFrames(-1)} className="control-btn frame-step-btn" title="Back 1 frame">
              ◀
            </button>

            {/* Back 30 frames - only in non-fullscreen */}
            {!isFullscreen && (
              <button onClick={() => seekByFrames(-30)} className="control-btn skip-btn" title="Back 30 frames">
                <SkipBack size={20} />
              </button>
            )}

            <button onClick={togglePlayPause} className="play-pause-btn">
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>

            {/* Forward 30 frames - only in non-fullscreen */}
            {!isFullscreen && (
              <button onClick={() => seekByFrames(30)} className="control-btn skip-btn" title="Forward 30 frames">
                <SkipForward size={20} />
              </button>
            )}

            {/* Forward 1 frame - always shown, use arrow symbol */}
            <button onClick={() => seekByFrames(1)} className="control-btn frame-step-btn" title="Forward 1 frame">
              ▶
            </button>

            {onDrawToggle && (
              <button
                onClick={onDrawToggle}
                className={`control-btn${drawMode ? ' active' : ''}`}
                title="Draw mode"
              >
                <Pencil size={18} />
              </button>
            )}

            <button
              onClick={toggleFullscreen}
              onTouchStart={handleLongPressStart}
              onTouchEnd={handleLongPressEnd}
              onTouchCancel={handleLongPressEnd}
              onMouseDown={handleLongPressStart}
              onMouseUp={handleLongPressEnd}
              onMouseLeave={handleLongPressEnd}
              className="control-btn"
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen (long-press for native on iOS)"}
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Settings panel removed as per user request */}
    </div>
  );
});

export default SynchronizedPosePlayer;
